import { PassportIconDisplay } from '@/components/passport/IconPicker';
import { ActivityDialog } from '@/components/passport/dialogs/ActivityDialog';
import { CreateSponsorDialog } from '@/components/passport/dialogs/CreateSponsorDialog';
import { EditEventDialog } from '@/components/passport/dialogs/EditEventDialog';
import { EditSponsorDialog } from '@/components/passport/dialogs/EditSponsorDialog';
import { SectionDialog } from '@/components/passport/dialogs/SectionDialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { apiFetch, messageFromBody } from '@/lib/api';
import { normalizeFilename } from '@/lib/filename';
import { useAuthStore } from '@/stores/auth-store';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, redirect } from '@tanstack/react-router';
import {
  type PassportActivityAdminDto,
  PassportEventDetailDto,
  type PassportEventDetailDto as PassportEventDetailDtoType,
  type PassportSectionAdminDto,
  PassportSponsorListDto,
} from '@tunodo/ticketing-shared';
import { ArrowLeft, Building2, Download, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

export const Route = createFileRoute('/_app/admin/passport/$eventId')({
  beforeLoad: () => {
    const { user } = useAuthStore.getState();
    if (user?.role !== 'ORGANIZER') {
      toast.info('Solo los organizadores pueden acceder al panel de pasaporte.');
      throw redirect({ to: '/' });
    }
  },
  component: PassportEventDetailPage,
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
async function downloadBlob(url: string, filename: string): Promise<void> {
  const token = useAuthStore.getState().token;
  const response = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) {
    let msg = response.statusText;
    try {
      msg = messageFromBody(await response.json()) ?? msg;
    } catch {
      // ignore
    }
    throw new Error(msg);
  }
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(objectUrl);
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
function PassportEventDetailPage() {
  const { eventId } = Route.useParams();
  const queryClient = useQueryClient();

  const [confirmState, setConfirmState] = useState<{
    open: boolean;
    title: string;
    description: string;
    onConfirm: () => void;
  }>({ open: false, title: '', description: '', onConfirm: () => {} });

  const openConfirm = (title: string, description: string, onConfirm: () => void) => {
    setConfirmState({ open: true, title, description, onConfirm });
  };

  const {
    data: event,
    isLoading,
    isError,
  } = useQuery<PassportEventDetailDtoType>({
    queryKey: ['passport-admin', 'event', eventId],
    queryFn: () =>
      apiFetch(`/api/passport-admin/events/${eventId}`, {
        schema: PassportEventDetailDto,
      }),
  });

  const deleteSectionMutation = useMutation({
    mutationFn: (sectionId: string) =>
      apiFetch(`/api/passport-admin/sections/${sectionId}`, { method: 'DELETE' }),
    onMutate: async (sectionId) => {
      await queryClient.cancelQueries({ queryKey: ['passport-admin', 'event', eventId] });
      const prev = queryClient.getQueryData<PassportEventDetailDtoType>([
        'passport-admin',
        'event',
        eventId,
      ]);
      if (prev) {
        queryClient.setQueryData<PassportEventDetailDtoType>(['passport-admin', 'event', eventId], {
          ...prev,
          sections: prev.sections.filter((s) => s.id !== sectionId),
        });
      }
      return { prev };
    },
    onError: (err: Error, _sectionId, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(['passport-admin', 'event', eventId], ctx.prev);
      }
      toast.error(err.message);
    },
    onSuccess: () => toast.success('Sección eliminada.'),
    onSettled: () =>
      void queryClient.invalidateQueries({ queryKey: ['passport-admin', 'event', eventId] }),
  });

  const deleteActivityMutation = useMutation({
    mutationFn: (activityId: string) =>
      apiFetch(`/api/passport-admin/activities/${activityId}`, { method: 'DELETE' }),
    onMutate: async (activityId) => {
      await queryClient.cancelQueries({ queryKey: ['passport-admin', 'event', eventId] });
      const prev = queryClient.getQueryData<PassportEventDetailDtoType>([
        'passport-admin',
        'event',
        eventId,
      ]);
      if (prev) {
        queryClient.setQueryData<PassportEventDetailDtoType>(['passport-admin', 'event', eventId], {
          ...prev,
          sections: prev.sections.map((s) => ({
            ...s,
            activities: s.activities.filter((a) => a.id !== activityId),
          })),
        });
      }
      return { prev };
    },
    onError: (err: Error, _activityId, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(['passport-admin', 'event', eventId], ctx.prev);
      }
      toast.error(err.message);
    },
    onSuccess: () => toast.success('Actividad eliminada.'),
    onSettled: () =>
      void queryClient.invalidateQueries({ queryKey: ['passport-admin', 'event', eventId] }),
  });

  const { data: sponsors = [] } = useQuery({
    queryKey: ['passport-admin', 'sponsors', eventId],
    queryFn: () =>
      apiFetch(`/api/passport-admin/events/${eventId}/sponsors`, {
        schema: PassportSponsorListDto,
      }),
  });

  const deleteSponsorMutation = useMutation({
    mutationFn: (sponsorId: string) =>
      apiFetch(`/api/passport-admin/sponsors/${sponsorId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['passport-admin', 'sponsors', eventId] });
      toast.success('Sponsor eliminado.');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const handleDownloadQr = async (activity: PassportActivityAdminDto) => {
    try {
      const label = activity.speakerName ?? activity.title;
      await downloadBlob(
        `/api/passport-admin/activities/${activity.id}/qr.png`,
        `qr-${normalizeFilename(label) || 'activity'}.png`,
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error al descargar el QR');
    }
  };

  const handleDeleteSection = (section: PassportSectionAdminDto) => {
    if (section.activities.length > 0) return;
    openConfirm(
      'Eliminar sección',
      `¿Eliminar la sección "${section.title}"? Esta acción no se puede deshacer.`,
      () => deleteSectionMutation.mutate(section.id),
    );
  };

  const handleDeleteActivity = (activityId: string) => {
    openConfirm(
      'Eliminar actividad',
      '¿Eliminar esta actividad? Esta acción no se puede deshacer.',
      () => deleteActivityMutation.mutate(activityId),
    );
  };

  if (isLoading) {
    return (
      <main className="flex-1 p-6 space-y-6">
        <div className="flex items-center gap-3">
          <Skeleton className="h-9 w-20" />
          <Skeleton className="h-7 w-64" />
        </div>
        <Card>
          <CardContent className="pt-6 space-y-2">
            {Array.from({ length: 4 }).map((_, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: skeleton rows
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </CardContent>
        </Card>
      </main>
    );
  }

  if (isError || !event) {
    return (
      <main className="flex-1 p-6 space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" asChild>
            <Link to="/admin/passport">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Volver
            </Link>
          </Button>
        </div>
        <Card className="border-destructive/50 bg-destructive/10">
          <CardHeader>
            <CardTitle className="text-destructive">Error al cargar el evento</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-destructive">No se pudo obtener el evento.</p>
          </CardContent>
        </Card>
      </main>
    );
  }

  return (
    <main className="flex-1 p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" asChild>
            <Link to="/admin/passport">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Volver
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{event.title}</h1>
            {event.date && (
              <p className="text-sm text-muted-foreground mt-0.5">
                {new Intl.DateTimeFormat('es-CO', { dateStyle: 'long' }).format(
                  new Date(`${event.date.slice(0, 10)}T12:00:00`),
                )}
              </p>
            )}
            <p className="text-xs text-muted-foreground mt-1">
              {event.sectionCount} secciones · {event.activityCount} actividades
            </p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <EditEventDialog event={event} />
          {/*
            Go no implementa DELETE /api/passport-admin/events/{eventId} (decision 193,
            apps/ticketing-api/cmd/api/inventory_test.go). La acción queda deshabilitada
            hasta que el backend la sirva, porque el router respondería 405.
          */}
          <Button
            variant="outline"
            size="sm"
            className="gap-1"
            disabled
            title="Eliminar eventos aún no está disponible en la API (decisión 193)"
          >
            <Trash2 className="h-4 w-4" />
            Eliminar evento
          </Button>
          <p className="w-full text-xs text-muted-foreground sm:text-right">
            Eliminar eventos aún no está disponible en la API.
          </p>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
          Secciones ({event.sections.length})
        </h2>
        <div className="flex gap-2">
          {event.sections.length > 0 && (
            <ActivityDialog
              eventId={event.id}
              sections={event.sections}
              trigger={
                <Button size="sm" variant="outline" className="gap-1">
                  <Plus className="h-4 w-4" />
                  Agregar actividad
                </Button>
              }
            />
          )}
          <SectionDialog
            eventId={event.id}
            trigger={
              <Button
                size="sm"
                className="gap-1 bg-cimoips-primary hover:bg-cimoips-primary/90 text-white"
              >
                <Plus className="h-4 w-4" />
                Agregar sección
              </Button>
            }
          />
        </div>
      </div>

      {/* Sections */}
      {event.sections.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            No hay secciones todavía. Crea la primera con "Agregar sección".
          </CardContent>
        </Card>
      ) : (
        <Accordion
          type="multiple"
          defaultValue={event.sections.map((s) => s.id)}
          className="space-y-3"
        >
          {event.sections.map((section) => (
            <AccordionItem
              key={section.id}
              value={section.id}
              className="border rounded-md bg-card"
            >
              <div className="flex items-center justify-between px-4">
                <AccordionTrigger className="flex-1 py-3 hover:no-underline">
                  <div className="flex items-center gap-3 text-left">
                    <PassportIconDisplay
                      icon={section.icon}
                      className="h-5 w-5 text-cimoips-primary shrink-0"
                    />
                    <div>
                      <div className="font-medium">{section.title}</div>
                      {section.description && (
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {section.description}
                        </div>
                      )}
                    </div>
                    <Badge variant="secondary" className="ml-2">
                      {section.activities.length}
                    </Badge>
                  </div>
                </AccordionTrigger>
                <div className="flex gap-1 shrink-0 ml-2">
                  <SectionDialog
                    eventId={event.id}
                    section={section}
                    trigger={
                      <Button variant="ghost" size="sm" className="gap-1">
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    }
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="gap-1 text-destructive hover:text-destructive"
                    onClick={() => handleDeleteSection(section)}
                    disabled={deleteSectionMutation.isPending || section.activities.length > 0}
                    title={
                      section.activities.length > 0
                        ? 'Elimina primero las actividades de esta sección'
                        : undefined
                    }
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
              <AccordionContent className="px-4 pb-4">
                <div className="rounded-md border overflow-x-auto">
                  <Table className="min-w-[640px]">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">#</TableHead>
                        <TableHead className="w-24">Tipo</TableHead>
                        <TableHead className="w-12">Ícono</TableHead>
                        <TableHead>Título</TableHead>
                        <TableHead>Ponente</TableHead>
                        <TableHead className="w-24">Código</TableHead>
                        <TableHead className="w-20">Sellos</TableHead>
                        <TableHead className="w-48">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {section.activities.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={8}
                            className="text-center py-6 text-muted-foreground text-sm"
                          >
                            Sin actividades.
                          </TableCell>
                        </TableRow>
                      ) : (
                        section.activities.map((activity) => (
                          <TableRow key={activity.id}>
                            <TableCell className="text-muted-foreground">
                              {activity.order}
                            </TableCell>
                            <TableCell>
                              <span
                                className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                                  activity.type === 'TALK'
                                    ? 'bg-purple-100 text-purple-700'
                                    : activity.type === 'SPONSOR'
                                      ? 'bg-amber-100 text-amber-700'
                                      : 'bg-slate-100 text-slate-700'
                                }`}
                              >
                                {activity.type === 'TALK'
                                  ? 'Charla'
                                  : activity.type === 'SPONSOR'
                                    ? 'Patrocinador'
                                    : 'Secundaria'}
                              </span>
                            </TableCell>
                            <TableCell>
                              <PassportIconDisplay icon={activity.icon} />
                            </TableCell>
                            <TableCell className="font-medium">{activity.title}</TableCell>
                            <TableCell className="text-muted-foreground">
                              {activity.speakerName ?? '—'}
                            </TableCell>
                            <TableCell>
                              {activity.shortCode ? (
                                <span className="font-mono text-sm font-bold tracking-widest text-purple-700">
                                  {activity.shortCode}
                                </span>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {activity.entryCount}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="gap-1"
                                  onClick={() => void handleDownloadQr(activity)}
                                >
                                  <Download className="h-3.5 w-3.5" />
                                  QR
                                </Button>
                                <Link
                                  to="/admin/passport/activities/$activityId"
                                  params={{ activityId: activity.id }}
                                >
                                  <Button variant="ghost" size="sm" className="gap-1">
                                    <Pencil className="h-3.5 w-3.5" />
                                  </Button>
                                </Link>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="gap-1 text-destructive hover:text-destructive"
                                  onClick={() => handleDeleteActivity(activity.id)}
                                  disabled={deleteActivityMutation.isPending}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
                <div className="mt-3 flex justify-end">
                  <ActivityDialog
                    eventId={event.id}
                    sections={event.sections}
                    defaultSectionId={section.id}
                    trigger={
                      <Button size="sm" variant="outline" className="gap-1">
                        <Plus className="h-3.5 w-3.5" />
                        Agregar actividad
                      </Button>
                    }
                  />
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}
      {/* ─── Apoyan (Sponsors) ─────────────────────────────────────────────── */}
      <Card className="mt-6">
        <CardHeader className="flex flex-row items-center justify-between py-4">
          <CardTitle className="text-base">Apoyan</CardTitle>
          <CreateSponsorDialog eventId={eventId} />
        </CardHeader>
        <CardContent className="p-0">
          {sponsors.length === 0 ? (
            <p className="px-6 pb-4 text-sm text-muted-foreground">
              No hay sponsors registrados aún.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">Logo</TableHead>
                  <TableHead>Nombre</TableHead>
                  <TableHead className="w-24">Fit</TableHead>
                  <TableHead className="w-16">Orden</TableHead>
                  <TableHead className="w-24 text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sponsors.map((sponsor) => (
                  <TableRow key={sponsor.id}>
                    <TableCell>
                      {sponsor.logoUrl ? (
                        <img
                          src={sponsor.logoUrl}
                          alt={sponsor.name}
                          className="h-8 w-8 rounded-full object-cover"
                        />
                      ) : (
                        <div className="flex h-8 w-8 items-center justify-center rounded-full border bg-muted">
                          <Building2 className="h-4 w-4 text-muted-foreground" />
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="font-medium">{sponsor.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{sponsor.fit}</Badge>
                    </TableCell>
                    <TableCell>{sponsor.order}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <EditSponsorDialog sponsor={sponsor} eventId={eventId} />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            openConfirm(
                              'Eliminar sponsor',
                              `¿Eliminar "${sponsor.name}"? Esta acción no se puede deshacer.`,
                              () => deleteSponsorMutation.mutate(sponsor.id),
                            )
                          }
                          disabled={deleteSponsorMutation.isPending}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={confirmState.open}
        onOpenChange={(open) => setConfirmState((s) => ({ ...s, open }))}
        title={confirmState.title}
        description={confirmState.description}
        onConfirm={confirmState.onConfirm}
      />
    </main>
  );
}
